import { useEffect, useState } from "react";
import { useLang, categoryName } from "@/i18n";
import { fetchNews, fetchTrending } from "@/lib/api";
import { HeroCard, NewsCard, ListCard } from "@/components/NewsCard";
import MostRead from "@/components/MostRead";
import SectionHeader from "@/components/SectionHeader";
import { Loader2 } from "lucide-react";

const CATEGORIES = [
  { slug: "gundem", emoji: "🔥" },
  { slug: "ekonomi", emoji: "📈" },
  { slug: "spor", emoji: "⚽" },
  { slug: "teknoloji", emoji: "💻" },
  { slug: "dunya", emoji: "🌍" },
  { slug: "saglik", emoji: "🏥" },
];

export default function Home() {
  const { lang, t } = useLang();
  const [hero, setHero] = useState(null);
  const [sideTop, setSideTop] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [trending, setTrending] = useState([]);
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const promises = [
          fetchNews({ lang, limit: 20 }),
          fetchTrending({ lang, limit: 5 }),
          ...CATEGORIES.map((c) => fetchNews({ lang, category: c.slug, limit: 6 })),
        ];
        const results = await Promise.all(promises);
        if (cancelled) return;
        const [allNews, trendingNews, ...catNews] = results;
        setHero(allNews[0] || null);
        setSideTop(allNews.slice(1, 4));
        setLatest(allNews.slice(4, 14));
        setTrending(trendingNews);
        const map = {};
        CATEGORIES.forEach((c, i) => { map[c.slug] = catNews[i]; });
        setByCategory(map);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 flex items-center justify-center" data-testid="home-loading">
        <Loader2 className="animate-spin text-red-600" size={32} />
        <span className="ml-3 text-neutral-600 font-mono uppercase tracking-wider text-sm">{t("loading")}</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="home-page">
      {/* HERO SECTION */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-8">
          {hero && <HeroCard item={hero} />}
        </div>
        <div className="lg:col-span-4 space-y-4">
          {sideTop.map((item) => (
            <NewsCard key={item.id} item={item} size="sm" />
          ))}
        </div>
      </section>

      {/* CATEGORY: GUNDEM */}
      <section className="mt-16">
        <SectionHeader category="gundem" emoji="🔥" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
          {(byCategory.gundem || []).slice(0, 6).map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* LATEST + MOST READ */}
      <section className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="flex items-end justify-between border-b-2 border-white pb-2 mb-6">
            <h2 className="headline text-3xl sm:text-4xl tracking-tight text-white">{t("latest")}</h2>
          </div>
          <div className="divide-y divide-neutral-800">
            {latest.map((item) => (
              <ListCard key={item.id} item={item} />
            ))}
          </div>
        </div>
        <div className="lg:col-span-4">
          <MostRead items={trending} />
        </div>
      </section>

      {/* OTHER CATEGORY SECTIONS */}
      {CATEGORIES.filter((c) => c.slug !== "gundem").map((c) => (
        <section key={c.slug} className="mt-20">
          <SectionHeader category={c.slug} emoji={c.emoji} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {(byCategory[c.slug] || []).slice(0, 3).map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          {(byCategory[c.slug] || []).length > 3 && (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
              {(byCategory[c.slug] || []).slice(3, 6).map((item) => (
                <ListCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
